import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { NotificationActionAPI } from '@makyoapp/makyo-sdk/api/Notifications'
import { UsersAPI } from '@makyoapp/makyo-sdk/api/Users'
import { NotificationActionTrigger, NotificationStatus } from '@makyoapp/makyo-sdk/models/Notifications'
import {
  IOrganizationMemberInviteMetadata,
  IOrganizationTransferOwnershipMetadata,
} from '@makyoapp/makyo-sdk/models/NotificationsMetaData'
import { Button, ButtonColor, JustifyVariant, Modal } from '@makyoapp/makyo-ui'

import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { useReqNotif } from '@/hooks/useReqNotif'
import { ORG_APP_ROUTE, ORG_ROUTE, USER_ROUTE } from '@/shared/constants'

import { SaveButton } from './SaveButton'

interface InvitationDeclineProps {
  isLoading: boolean
  setStep: (nextStep: number) => void
  action: NotificationActionTrigger | null
  data: IOrganizationMemberInviteMetadata | IOrganizationTransferOwnershipMetadata
  inviteeOrgId?: string
}

export function InvitationDecline({ isLoading, setStep, action, data }: InvitationDeclineProps) {
  const { t } = useTranslation()
  const printAPIError = useReqError()

  return (
    <Button
      disabled={isLoading}
      colorStyle={ButtonColor.Danger}
      onClick={() => {
        setStep(2)
        if (action && data) {
          NotificationActionAPI.ProcessAction(action, data, NotificationStatus.Declined).catch((e) => {
            printAPIError(e)
          })
        }
      }}
    >
      {t('Decline Invitation')}
    </Button>
  )
}
interface InvitationAcceptProps {
  isLoading: boolean
  setIsLoading: (nextLoading: boolean) => void
  action: NotificationActionTrigger | null
  data: IOrganizationMemberInviteMetadata | IOrganizationTransferOwnershipMetadata
  inviteeOrgId?: string
  onHide: () => void
}

export function InvitationAccept({
  isLoading,
  setIsLoading,
  action,
  data,
  inviteeOrgId,
  onHide,
}: InvitationAcceptProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { updateOrgs, updateOrgsPerms } = useOrgsContext()
  const printAPIError = useReqError()

  return (
    <SaveButton
      isLoading={isLoading}
      onClick={() => {
        setIsLoading(true)
        if (action && data) {
          NotificationActionAPI.ProcessAction(action, data, NotificationStatus.Accepted)
            .then(() => {
              return updateOrgs().then((orgs) => {
                updateOrgsPerms(orgs)
              })
            })
            .then(() => {
              if (inviteeOrgId) {
                navigate(`${ORG_ROUTE}/${inviteeOrgId}`)
              } else {
                navigate(`${USER_ROUTE}`)
              }
            })
            .catch((e) => {
              navigate(`${USER_ROUTE}`)
              printAPIError(e)
            })
            .finally(onHide)
        }
      }}
      label={t('Accept Invitation')}
    />
  )
}

interface InvitationFooterEndProps {
  isLoading: boolean
  onHide: () => void
}

export function InvitationFooterEnd({ isLoading, onHide }: InvitationFooterEndProps) {
  const { logout, userData } = useAuth()
  const { t } = useTranslation()
  const { orgs } = useOrgsContext()
  const myOrg = orgs.find((o) => o.isDefault && o.ownerId === userData?.id)
  const navigate = useNavigate()

  /// declined invitation and is an existing user
  return (
    <>
      <Button colorStyle={ButtonColor.Danger} onClick={logout}>
        {t('Logout')}
      </Button>
      <Button
        disabled={isLoading}
        onClick={() => {
          onHide()
          if (myOrg) {
            navigate(`${ORG_ROUTE}/${myOrg.id}/${ORG_APP_ROUTE}`)
          }
        }}
      >
        {t('Manage Apps')}
      </Button>
    </>
  )
}

interface InvitationNotMatchProps {
  show: boolean
  onIgnore: () => void
}

export function InvitationNotMatch({ show, onIgnore }: InvitationNotMatchProps) {
  const { t } = useTranslation()
  const { logout } = useAuth()

  return (
    <Modal show={show}>
      <Modal.Body className='modal-sm'>
        <div className='mb-2 text-base'>{t("The email on the invitation doesn't match this account.")}</div>
        <div>{t('What would you like to do next?')}</div>
      </Modal.Body>
      <Modal.Footer className='flex-c' justify={JustifyVariant.End}>
        <Button colorStyle={ButtonColor.Secondary} onClick={logout}>
          {t('Logout')}
        </Button>
        <Button onClick={onIgnore}>{t('Ignore')}</Button>
      </Modal.Footer>
    </Modal>
  )
}

export function InvitationProcessed({ status }: { status?: NotificationStatus }) {
  const { t } = useTranslation()
  let text = t('This invitation has been already accepted!')
  if (status === NotificationStatus.Declined) {
    text = t('This invitation has been already declined!')
  }
  return <div className='text-base'>{text}</div>
}

interface Props {
  show: boolean
}

export default function InvitationModal({ show }: Props) {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const [step, setStep] = useState(1)

  const { action, data, showNotMatch, onHideWithClean, onHide, isLoading, setIsLoading, isOnBoarding, invitation } =
    useReqNotif(NotificationActionTrigger.OrganizationMemberInvite)

  const invProcessed =
    invitation?.status === NotificationStatus.Accepted || invitation?.status === NotificationStatus.Declined

  const orgName = data?.OrgName
  const inviteeOrgId = data?.OrgID

  const creatAppButton = <Button onClick={onHideWithClean}>{t('Try Makyo')}</Button>

  return (
    <>
      <InvitationNotMatch show={showNotMatch} onIgnore={onHideWithClean} />

      <Modal show={show}>
        <Modal.Body className='modal-sm'>
          {invProcessed ? (
            <InvitationProcessed status={invitation?.status} />
          ) : (
            <div className='text-base'>
              {step === 1 && (
                <Trans i18nKey='invitedToJoin' values={{ orgName }}>
                  You&apos;re invited to join <strong>{orgName}</strong>!
                </Trans>
              )}
              {step === 2 && (
                <>
                  <div className='mb-2'>
                    <Trans i18nKey='declinedInvitation' values={{ orgName }}>
                      You&apos;ve declined the invitation to join <strong>{orgName}</strong>!
                    </Trans>
                  </div>
                  <div>{t('What would you like to do next?')}</div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer justify={JustifyVariant.End} className='gap-4'>
          {invProcessed && <Button onClick={onHideWithClean}>{t('Ok')}</Button>}
          {!invProcessed && step === 1 && (
            <>
              <InvitationDecline isLoading={isLoading} setStep={setStep} action={action} data={data} />
              <InvitationAccept
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                onHide={onHide}
                inviteeOrgId={inviteeOrgId}
                action={action}
                data={data}
              />
            </>
          )}
          {
            /// declined invitation and is a new user
            !invProcessed && step === 2 && isOnBoarding && (
              <>
                <SaveButton
                  isLoading={isLoading}
                  colorStyle={ButtonColor.Danger}
                  onClick={() => {
                    setIsLoading(true)
                    UsersAPI.ReqDelete()
                      .catch((e) => {})
                      .finally(() => {
                        setIsLoading(false)
                        logout()
                      })
                  }}
                  label={t('Delete Account')}
                />
                {creatAppButton}
              </>
            )
          }
          {
            /// declined invitation and is an existing user
            !invProcessed && step === 2 && !isOnBoarding && (
              <InvitationFooterEnd isLoading={isLoading} onHide={onHideWithClean} />
            )
          }
        </Modal.Footer>
      </Modal>
    </>
  )
}
